import { mapValues } from "lodash"
import type {
  ContributorNode,
  ContributorsQueryOptions,
  CreateContributorsQuery,
} from "./contributors-query-types"

export type { ContributorNode, ContributorsQueryOptions, CreateContributorsQuery } from "./contributors-query-types"

// Keys stored alongside factor ids in the values object that are NOT factor ids themselves
// and must be preserved when filtering. Performance factor ids never end in "Return"; risk
// factor ids are never any of the exact strings below.
const NON_FACTOR_EXACT_KEYS = new Set([
  "descriptors", "totalContribution", "totalPredictedRisk", "specific", "factors",
])

function isNonFactorKey(key: string): boolean {
  return NON_FACTOR_EXACT_KEYS.has(key) || key.endsWith("Return")
}

export function filterNodeToFactors(
  node: ContributorNode,
  prefix: string,
  factorIds: string[],
): ContributorNode {
  return filterNodeToFactorsWithSet(node, prefix, new Set(factorIds))
}

function filterNodeToFactorsWithSet(
  node: ContributorNode,
  prefix: string,
  factorIdSet: Set<string>,
): ContributorNode {
  const originalValues = node.values?.[prefix] ?? {}

  // Handle both flat {factorId: value} and category-nested {categoryId: {factorId: value}}
  // structures. Content-set gatherers store data nested by category when a selectedCategory
  // is passed; all other gatherers store flat by factor id. We must not strip category
  // containers since their keys are category names (not factor ids).
  const filteredEntries = Object.entries(originalValues).flatMap(
    ([id, value]): [string, unknown][] => {
      if (isNonFactorKey(id)) return [[id, value]]
      if (factorIdSet.has(id)) return [[id, value]]
      if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        const subFiltered = Object.fromEntries(
          Object.entries(value as Record<string, unknown>).filter(([subId]) => factorIdSet.has(subId)),
        )
        if (Object.keys(subFiltered).length > 0) return [[id, subFiltered]]
      }
      return []
    },
  )
  const filteredFactorValues = Object.fromEntries(filteredEntries)

  const filteredChildren = node.children
    ? mapValues(node.children, child => filterNodeToFactorsWithSet(child, prefix, factorIdSet))
    : undefined

  return {
    ...node,
    values: { ...node.values, [prefix]: filteredFactorValues },
    ...(filteredChildren !== undefined && { children: filteredChildren }),
  }
}

export function createDrilldownContributorsQuery(
  createBaseQuery: CreateContributorsQuery,
  factorIds: string[],
  options: ContributorsQueryOptions,
) {
  const baseQuery = createBaseQuery(options)

  if (factorIds.length === 0) {
    return baseQuery
  }

  return {
    ...baseQuery,
    select: (data: unknown) => {
      const result = baseQuery.select(data)
      if (!result) return result
      return filterNodeToFactors(result, options.prefix, factorIds)
    },
  }
}
